import { useMutation } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { contractHistoryVariables } from '../lib/contracts'
import type { CostContract, CostContractDiscountInput, CostContractLineInput, CostContractPriceItemInput, CostContractStatus } from '../types'
import { WRITE, useCostWriteQueue, type Mutate, type Reload, type Vars } from './useCostWriteQueue'

/**
 * Contract and attachment writes (spec §6). A write on a contract reloads its detail and its history, also after a
 * refusal; `onDetailError` hears when the detail reload itself fails (the contract may be gone).
 * The register and the budget refetch on their own when they are shown again (cache-and-network).
 */
export function useContractMutations(projectId: string, onDetailError?: (contractId: string, error: unknown) => void) {
  const { saving, run } = useCostWriteQueue()
  const [create] = useMutation(Mutation.CREATE_COST_CONTRACT, WRITE)
  const [createFromTender] = useMutation(Mutation.CREATE_COST_CONTRACT_FROM_TENDER, WRITE)
  const [update] = useMutation(Mutation.UPDATE_COST_CONTRACT, WRITE)
  const [setLines] = useMutation(Mutation.SET_COST_CONTRACT_LINES, WRITE)
  const [setPriceItems] = useMutation(Mutation.SET_COST_CONTRACT_PRICE_ITEMS, WRITE)
  const [importPriceItems] = useMutation(Mutation.IMPORT_COST_CONTRACT_PRICE_ITEMS_FROM_TENDER, WRITE)
  const [setStatus] = useMutation(Mutation.SET_COST_CONTRACT_STATUS, WRITE)
  const [remove] = useMutation(Mutation.DELETE_COST_CONTRACT, WRITE)
  const [addAttachment] = useMutation(Mutation.ADD_COST_ATTACHMENT, WRITE)
  const [removeAttachment] = useMutation(Mutation.REMOVE_COST_ATTACHMENT, WRITE)
  const [addRelease] = useMutation(Mutation.ADD_COST_RETENTION_RELEASE, WRITE)
  const [deleteRelease] = useMutation(Mutation.DELETE_COST_RETENTION_RELEASE, WRITE)
  const [addRepresentative] = useMutation(Mutation.ADD_COST_CONTRACT_REPRESENTATIVE, WRITE)
  const [removeRepresentative] = useMutation(Mutation.REMOVE_COST_CONTRACT_REPRESENTATIVE, WRITE)
  const [linkSubmittals] = useMutation(Mutation.LINK_COST_CONTRACT_SUBMITTALS, WRITE)
  const [unlinkSubmittal] = useMutation(Mutation.UNLINK_COST_CONTRACT_SUBMITTAL, WRITE)
  const detail = (id: string): Reload[] => [
    { query: Query.COST_CONTRACT, variables: { id }, onError: onDetailError && ((e: unknown) => onDetailError(id, e)) },
    { query: Query.COST_ACTIVITY_LOG, variables: contractHistoryVariables(projectId, id) },
  ]
  const m = (fn: unknown) => fn as Mutate

  return {
    saving,
    create: (input: Vars) => run<{ createCostContract: CostContract }>(m(create), { projectId, input }, []),
    createFromTender: (tenderId: string) =>
      run<{ createCostContractFromTender: { contract: CostContract; awardedAmount: string | null; itemCount: number } }>(m(createFromTender), { projectId, tenderId }, []),
    update: (id: string, input: Vars) => run(m(update), { id, input }, detail(id)),
    setLines: (id: string, lines: CostContractLineInput[]) => run(m(setLines), { id, lines }, detail(id)),
    setPriceItems: (id: string, items: CostContractPriceItemInput[], discount: CostContractDiscountInput | null) => run(m(setPriceItems), { id, items, discount }, detail(id)),
    importPriceItems: (id: string) => run(m(importPriceItems), { id }, detail(id)),
    /** `from` is the status the person saw: the server refuses the change when it no longer holds (spec §11). */
    setStatus: (id: string, status: CostContractStatus, from: CostContractStatus) => run(m(setStatus), { id, status, from }, detail(id)),
    // The register's unfiltered list is reloaded so the deleted contract does not flash back from the cache;
    // a refused delete shows what is there now.
    remove: (id: string) => run(m(remove), { id }, [{ query: Query.COST_CONTRACTS, variables: { projectId, filter: {} } }], detail(id)),
    addAttachment: (contractId: string, source: { fileId: string } | { docRevisionId: string }) =>
      run(m(addAttachment), { target: { contractId }, ...source }, detail(contractId)),
    removeAttachment: (contractId: string, id: string) => run(m(removeAttachment), { id }, detail(contractId)),
    addRelease: (contractId: string, input: Vars) => run(m(addRelease), { contractId, input }, detail(contractId)),
    deleteRelease: (contractId: string, id: string) => run(m(deleteRelease), { id }, detail(contractId)),
    addRepresentative: (contractId: string, userId: string) => run(m(addRepresentative), { contractId, userId }, detail(contractId)),
    removeRepresentative: (contractId: string, id: string) => run(m(removeRepresentative), { id }, detail(contractId)),
    linkSubmittals: (contractId: string, submittalIds: string[]) => run(m(linkSubmittals), { contractId, submittalIds }, detail(contractId)),
    unlinkSubmittal: (contractId: string, submittalId: string) => run(m(unlinkSubmittal), { contractId, submittalId }, detail(contractId)),
    }
}

export type ContractMutations = ReturnType<typeof useContractMutations>
